'use client';

import { useEffect, useId, useRef, useState } from 'react';

export type CommandMenuItem = {
  id: string;
  label: string;
  onClick: () => void | Promise<void>;
  destructive?: boolean;
  disabled?: boolean;
};

type Props = {
  items: CommandMenuItem[];
  ariaLabel: string;
  className?: string;
};

export function CommandMenu({ items, ariaLabel, className }: Props) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className={`command-menu${className ? ` ${className}` : ''}`} ref={rootRef}>
      <button
        type="button"
        className="btn btn-ghost command-menu__trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <i className="uil uil-ellipsis-h" aria-hidden />
      </button>
      {open && (
        <div id={menuId} className="command-menu__popup" role="menu">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className={`command-menu__item${item.destructive ? ' command-menu__item--destructive' : ''}`}
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                void item.onClick();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
